import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { formatINR } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Target, Sparkles, Plus } from "lucide-react";

const RETURN_CHOICES = [
  { k: "Safe", r: 0.07, d: "FD / debt" },
  { k: "Balanced", r: 0.11, d: "mixed funds" },
  { k: "Growth", r: 0.13, d: "equity-heavy" },
];
const TARGET_CHIPS = [500000, 1000000, 2500000, 5000000, 10000000];
const YEAR_CHIPS = [3, 5, 10, 15, 20];

function requiredMonthly(fv, annualRate, years) {
  const i = annualRate / 12;
  const n = years * 12;
  if (n <= 0) return fv;
  const factor = ((Math.pow(1 + i, n) - 1) / i) * (1 + i); // SIP at start of month
  const p = fv / factor;
  return Math.ceil(p / 100) * 100;
}

export default function GoalPlanner({ onCreate }) {
  const [target, setTarget] = useState(1000000);
  const [years, setYears] = useState(5);
  const [choice, setChoice] = useState(RETURN_CHOICES[1]);

  const { monthly, invested, growth, lowerMonthly } = useMemo(() => {
    const fv = Number(target) || 0;
    const m = requiredMonthly(fv, choice.r, years);
    const inv = m * years * 12;
    const lower = requiredMonthly(fv, Math.max(0.04, choice.r - 0.03), years);
    return { monthly: m, invested: inv, growth: Math.max(0, fv - inv), lowerMonthly: lower };
  }, [target, years, choice]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8 soft-shadow mb-8"
      data-testid="goal-planner"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-indigo-500" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-500">Goal planner</p>
      </div>
      <h3 className="font-heading font-semibold text-xl text-slate-900 mb-6">
        How much should I save each month?
      </h3>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Target */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-600">I want to reach</label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-mono text-slate-400">₹</span>
              <Input type="number" data-testid="planner-target-input" value={target}
                onChange={(e) => setTarget(e.target.value === "" ? "" : Number(e.target.value))}
                className="h-9 w-36 pl-6 font-mono text-right rounded-xl" />
            </div>
          </div>
          <Slider data-testid="planner-target-slider" min={100000} max={10000000} step={100000}
            value={[Math.min(10000000, Number(target) || 0)]} onValueChange={(v) => setTarget(v[0])} className="mt-3" />
          <div className="flex flex-wrap gap-1.5 mt-3">
            {TARGET_CHIPS.map((t) => (
              <button key={t} data-testid={`planner-target-${t}`} onClick={() => setTarget(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-200 ${
                  Number(target) === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}>
                {t >= 10000000 ? "₹1Cr" : `₹${t / 100000}L`}
              </button>
            ))}
          </div>
        </div>

        {/* Years */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-600">In</label>
            <span className="text-sm font-mono font-semibold text-slate-900">{years} {years === 1 ? "year" : "years"}</span>
          </div>
          <Slider data-testid="planner-years-slider" min={1} max={30} step={1}
            value={[years]} onValueChange={(v) => setYears(v[0])} className="mt-3" />
          <div className="flex flex-wrap gap-1.5 mt-3">
            {YEAR_CHIPS.map((y) => (
              <button key={y} data-testid={`planner-year-${y}`} onClick={() => setYears(y)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-200 ${
                  years === y ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}>{y}y</button>
            ))}
          </div>
        </div>
      </div>

      {/* Return assumption */}
      <div className="mb-6">
        <label className="text-sm font-medium text-slate-600 mb-2 block">Expected return style</label>
        <div className="grid grid-cols-3 gap-2">
          {RETURN_CHOICES.map((c) => (
            <button key={c.k} data-testid={`planner-return-${c.k}`} onClick={() => setChoice(c)}
              className={`p-3 rounded-2xl border text-left transition-colors duration-200 ${
                choice.k === c.k ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <p className="font-semibold text-slate-900 text-sm">{c.k}</p>
              <p className="text-[11px] text-slate-400">{c.d} · ~{Math.round(c.r * 100)}%</p>
            </button>
          ))}
        </div>
      </div>

      {/* Result */}
      <div className="rounded-2xl bg-slate-900 p-6 text-white relative overflow-hidden">
        <div className="absolute -top-14 -right-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
        <div className="relative z-10">
          <p className="text-sm text-slate-300 mb-1">You'd need to invest about</p>
          <p className="font-mono font-bold text-4xl tracking-tight" data-testid="planner-monthly">
            {formatINR(monthly)}<span className="text-lg font-sans text-slate-400 font-medium"> / month</span>
          </p>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed">
            Over {years} {years === 1 ? "year" : "years"} you'd invest {formatINR(invested)} and aim for
            about {formatINR(growth)} of potential growth to reach {formatINR(Number(target) || 0)}.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            If returns run lower, you'd need closer to {formatINR(lowerMonthly)}/month. Estimates only — no investment is guaranteed.
          </p>
        </div>
      </div>

      {onCreate && (
        <Button
          data-testid="planner-create-goal"
          onClick={() => onCreate({ target: Number(target) || 0, years, monthly })}
          className="rounded-full gap-2 mt-5 bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus size={17} /> Save as a goal
        </Button>
      )}
    </motion.div>
  );
}
